import React, { useState } from "react";
import { ConfirmModal } from "../turnos/TurnosUi";
import { useClientesApi } from "./clientesApi";
import { KIND_LABEL, MAX_NOTE_LENGTH, errorDeApi } from "./clientesUtils";
import useCarga from "./useCarga";
import "./clientes.css";

/* Notas de la ficha: línea de tiempo (más nueva primero) con "General" y "Ficha técnica" (fórmulas, tratamientos).
   Editar y archivar dependen de `canEdit`, que decide el servidor (autor o dueño). */

const FILTROS = [
  { id: "all", label: "Todas" },
  { id: "general", label: KIND_LABEL.general },
  { id: "technical", label: KIND_LABEL.technical },
];

const fechaHora = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
};

const mensajeDeNota = (e, fallback) => {
  const { message, codigo, status } = errorDeApi(e, fallback);
  if (codigo === "note_too_long") return `La nota supera los ${MAX_NOTE_LENGTH} caracteres.`;
  if (codigo === "not_note_author") return "Solo el autor de la nota o el dueño pueden modificarla.";
  if (status === 403) return "No tenés permiso para modificar las notas de este cliente.";
  if (status === 404) return "La nota o la ficha ya no existen. Actualizá la pantalla.";
  return message;
};

const Contador = ({ largo }) => (
  <div className={"fc-counter" + (largo > MAX_NOTE_LENGTH ? " fc-counter--over" : "")} aria-live="polite">
    {largo} / {MAX_NOTE_LENGTH}
  </div>
);

const ClienteNotas = ({ clientId }) => {
  const api = useClientesApi();
  const notas = useCarga(() => api.notes(clientId), clientId, {
    valida: (info) => Array.isArray(info.items),
    mensaje: "No pudimos cargar las notas. Probá de nuevo.",
  });
  const [filtro, setFiltro] = useState("all");
  const [nueva, setNueva] = useState({ kind: "general", body: "" });
  const [guardando, setGuardando] = useState(false);
  const [errorNueva, setErrorNueva] = useState("");
  const [edicion, setEdicion] = useState(null); // { id, kind, body }
  const [errorEdicion, setErrorEdicion] = useState("");
  const [paraArchivar, setParaArchivar] = useState(null);
  const [errorArchivo, setErrorArchivo] = useState("");

  const agregar = async (e) => {
    e.preventDefault();
    const body = nueva.body.trim();
    if (!body || body.length > MAX_NOTE_LENGTH) return;
    setGuardando(true);
    setErrorNueva("");
    try {
      await api.addNote(clientId, { kind: nueva.kind, body });
      setNueva((prev) => ({ ...prev, body: "" }));
      await notas.reload();
    } catch (err) {
      setErrorNueva(mensajeDeNota(err, "No pudimos guardar la nota. Probá de nuevo."));
    } finally {
      setGuardando(false);
    }
  };

  const guardarEdicion = async () => {
    const body = edicion.body.trim();
    if (!body || body.length > MAX_NOTE_LENGTH) return;
    setGuardando(true);
    setErrorEdicion("");
    try {
      await api.updateNote(clientId, edicion.id, { body, kind: edicion.kind });
      setEdicion(null);
      await notas.reload();
    } catch (err) {
      setErrorEdicion(mensajeDeNota(err, "No pudimos guardar los cambios de la nota."));
    } finally {
      setGuardando(false);
    }
  };

  const archivar = async () => {
    setGuardando(true);
    setErrorArchivo("");
    try {
      await api.archiveNote(clientId, paraArchivar._id);
      setParaArchivar(null);
      await notas.reload();
    } catch (err) {
      setErrorArchivo(mensajeDeNota(err, "No pudimos archivar la nota. Probá de nuevo."));
    } finally {
      setGuardando(false);
    }
  };

  const items = (notas.data?.items || []).filter((n) => filtro === "all" || n.kind === filtro);
  const largoNueva = nueva.body.length;

  return (
    <section className="fc-card" aria-labelledby="fc-notas-title">
      <h3 id="fc-notas-title">Notas</h3>

      <form onSubmit={agregar}>
        <div className="fc-kind-select" role="radiogroup" aria-label="Tipo de nota">
          {["general", "technical"].map((k) => (
            <label key={k} className="fc-check">
              <input type="radio" name="fc-nota-tipo" checked={nueva.kind === k} onChange={() => setNueva((prev) => ({ ...prev, kind: k }))} />
              <span>{KIND_LABEL[k]}</span>
            </label>
          ))}
        </div>
        <textarea
          className="zr-textarea"
          style={{ minHeight: 90 }}
          aria-label="Nueva nota"
          placeholder={nueva.kind === "technical" ? "Fórmula, tratamiento, producto usado…" : "Algo para recordar de este cliente…"}
          value={nueva.body}
          onChange={(e) => setNueva((prev) => ({ ...prev, body: e.target.value }))}
        />
        <Contador largo={largoNueva} />
        {errorNueva && <div className="fc-error" role="alert">{errorNueva}</div>}
        <div className="fc-actions">
          <button type="submit" className="zr-btn zr-btn--dark" disabled={guardando || !nueva.body.trim() || largoNueva > MAX_NOTE_LENGTH}>
            Agregar nota
          </button>
        </div>
      </form>

      <div className="fc-actions" role="group" aria-label="Filtrar notas">
        {FILTROS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={"zr-btn " + (filtro === f.id ? "zr-btn--dark" : "zr-btn--soft")}
            aria-pressed={filtro === f.id}
            onClick={() => setFiltro(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {notas.loading && !notas.data && <p role="status">Cargando notas…</p>}
      {notas.error && (
        <div className="fc-error" role="alert">
          {notas.error.message}
          <button type="button" className="zr-btn zr-btn--soft" onClick={notas.reload}>Reintentar</button>
        </div>
      )}
      {notas.data && items.length === 0 && (
        <div className="fc-empty">
          <p>{filtro === "all" ? "Todavía no hay notas de este cliente." : `No hay notas de tipo "${KIND_LABEL[filtro]}".`}</p>
          <p>Escribí la primera arriba: queda con tu nombre y la fecha.</p>
        </div>
      )}
      {notas.data?.truncated && <p className="fc-hint">Mostramos las 200 notas más recientes.</p>}

      {items.length > 0 && (
        <ul className="fc-notes" aria-label="Notas del cliente">
          {items.map((n) => {
            const editando = edicion?.id === n._id;
            return (
              <li key={n._id} className={"fc-note" + (n.kind === "technical" ? " fc-note--technical" : "")}>
                <div className="fc-note__head">
                  <span className="fc-note__kind">{KIND_LABEL[n.kind] || n.kind}</span>
                  <span>{n.mine ? "Vos" : n.authorName || "—"}</span>
                  <span>{fechaHora(n.createdAt)}</span>
                  {n.editedAt && <span>(editada)</span>}
                </div>
                {editando ? (
                  <>
                    <select
                      className="zr-input"
                      aria-label="Tipo de la nota"
                      value={edicion.kind}
                      onChange={(e) => setEdicion((prev) => ({ ...prev, kind: e.target.value }))}
                    >
                      <option value="general">{KIND_LABEL.general}</option>
                      <option value="technical">{KIND_LABEL.technical}</option>
                    </select>
                    <textarea
                      className="zr-textarea"
                      style={{ minHeight: 90, marginTop: 8 }}
                      aria-label="Editar nota"
                      value={edicion.body}
                      onChange={(e) => setEdicion((prev) => ({ ...prev, body: e.target.value }))}
                    />
                    <Contador largo={edicion.body.length} />
                    {errorEdicion && <div className="fc-error" role="alert">{errorEdicion}</div>}
                    <div className="fc-note__actions">
                      <button
                        type="button"
                        className="zr-btn zr-btn--dark"
                        disabled={guardando || !edicion.body.trim() || edicion.body.length > MAX_NOTE_LENGTH}
                        onClick={guardarEdicion}
                      >
                        Guardar
                      </button>
                      <button type="button" className="zr-btn zr-btn--ghost" disabled={guardando} onClick={() => setEdicion(null)}>Cancelar</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="fc-note__body">{n.body}</p>
                    {n.canEdit && (
                      <div className="fc-note__actions">
                        <button
                          type="button"
                          className="zr-btn zr-btn--soft"
                          onClick={() => {
                            setErrorEdicion("");
                            setEdicion({ id: n._id, kind: n.kind, body: n.body });
                          }}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="zr-btn zr-btn--ghost"
                          onClick={() => {
                            setErrorArchivo("");
                            setParaArchivar(n);
                          }}
                        >
                          Archivar
                        </button>
                      </div>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {paraArchivar && (
        <ConfirmModal
          title="Archivar nota"
          confirmLabel="Archivar"
          busy={guardando}
          onConfirm={archivar}
          onCancel={() => setParaArchivar(null)}
        >
          La nota deja de mostrarse en la ficha. No se borra.
          {errorArchivo && <span className="fc-field-error" role="alert"> {errorArchivo}</span>}
        </ConfirmModal>
      )}
    </section>
  );
};

export default ClienteNotas;
